import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';
import { ApiError } from '@hutdrop/api-client';
import type { StaffMember } from '@hutdrop/shared';
import { Alert, Button, Dialog, Empty, PageHeader, SelectField, Skeleton, TextField } from '../components/ui';
import { api } from '../lib/api';
import { useStaffAuth } from '../lib/auth';

const ROLE_LABEL: Record<StaffMember['role'], string> = { EMPLOYEE: 'Employee', MANAGER: 'Manager', ADMIN: 'Owner / Admin' };
const ROLE_HINT: Record<StaffMember['role'], string> = {
  EMPLOYEE: 'Orders, scanning, pickup, receiving and adjusting stock.',
  MANAGER: 'Everything an employee does, plus products, deals, coupons, reports, customers, audit log and store hours.',
  ADMIN: 'Everything, plus store details, settings and staff accounts.',
};
const when = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const errMsg = (e: unknown): string => (e instanceof ApiError ? (Object.values(e.fieldErrors)[0] ?? e.message) : 'Something went wrong.');

export function StaffPage() {
  const { staff } = useStaffAuth();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['staff'], queryFn: () => api.request<{ items: StaffMember[] }>('/v1/admin/staff', { auth: true }).then((r) => r.items) });
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<StaffMember | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function update(m: StaffMember, patch: Partial<Pick<StaffMember, 'role' | 'status'>>, message: string) {
    setError(null);
    setDone(null);
    try {
      await api.request(`/v1/admin/staff/${m.id}`, { method: 'PATCH', auth: true, body: patch });
      await qc.invalidateQueries({ queryKey: ['staff'] });
      setDone(message);
    } catch (err) {
      setError(errMsg(err));
    }
  }

  return (
    <div className="page page--wide">
      <PageHeader
        title="Staff"
        subtitle="Who can sign in to this dashboard and what they can do. Every change is in the audit log."
        actions={
          <Button onClick={() => setAdding(true)}>
            <UserPlus size={16} aria-hidden="true" /> Add staff member
          </Button>
        }
      />
      {error ? <Alert>{error}</Alert> : null}
      {done ? <Alert tone="success">{done}</Alert> : null}
      {list.isPending ? (
        <Skeleton />
      ) : list.isError ? (
        <Alert>{list.error.message}</Alert>
      ) : list.data.length === 0 ? (
        <Empty icon={<Users size={28} />} title="No staff yet." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Last sign-in</th>
                <th scope="col">
                  <span className="sd-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((m) => {
                const self = m.id === staff?.user.id;
                return (
                  <tr key={m.id}>
                    <td>
                      {m.firstName} {m.lastName ?? ''}
                      {self ? <span className="muted small"> (you)</span> : null}
                    </td>
                    <td>{m.email}</td>
                    <td>
                      {self ? (
                        ROLE_LABEL[m.role]
                      ) : (
                        <select
                          className="fld__input fld__input--sm"
                          aria-label={`Role for ${m.firstName}`}
                          value={m.role}
                          onChange={(e) => void update(m, { role: e.target.value as StaffMember['role'] }, `${m.firstName} is now ${ROLE_LABEL[e.target.value as StaffMember['role']]}. They were signed out and need to sign in again.`)}
                        >
                          {(['EMPLOYEE', 'MANAGER', 'ADMIN'] as const).map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABEL[r]}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className={m.status === 'DISABLED' ? 'text-danger' : ''}>{m.status === 'ACTIVE' ? 'Active' : 'Disabled'}</td>
                    <td className="nowrap muted">{m.lastLoginAt ? when.format(new Date(m.lastLoginAt)) : 'Never'}</td>
                    <td className="nowrap">
                      {self ? null : (
                        <>
                          <button type="button" className="link-btn" onClick={() => setResetting(m)}>
                            Reset password
                          </button>{' '}
                          ·{' '}
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() =>
                              void update(m, { status: m.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' }, m.status === 'ACTIVE' ? `${m.firstName} can no longer sign in and was signed out everywhere.` : `${m.firstName} can sign in again.`)
                            }
                          >
                            {m.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ul className="muted small role-legend">
        {(['EMPLOYEE', 'MANAGER', 'ADMIN'] as const).map((r) => (
          <li key={r}>
            <strong>{ROLE_LABEL[r]}:</strong> {ROLE_HINT[r]}
          </li>
        ))}
      </ul>

      <AddStaffDialog open={adding} onClose={() => setAdding(false)} onAdded={(name) => setDone(`${name} was added. Give them their email and temporary password; they should change it after signing in.`)} />
      <ResetPasswordDialog member={resetting} onClose={() => setResetting(null)} onDone={(name) => setDone(`New temporary password set for ${name}. They were signed out everywhere.`)} />
    </div>
  );
}

function AddStaffDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (name: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', role: 'EMPLOYEE' as StaffMember['role'], password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request('/v1/admin/staff', { method: 'POST', auth: true, body: { ...f, lastName: f.lastName.trim() || undefined } });
      await qc.invalidateQueries({ queryKey: ['staff'] });
      onAdded(f.firstName);
      setF({ firstName: '', lastName: '', email: '', role: 'EMPLOYEE', password: '' });
      onClose();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onClose={onClose} title="Add staff member">
      <form className="form-grid" onSubmit={submit}>
        {error ? <Alert>{error}</Alert> : null}
        <div className="form-row">
          <TextField id="st-first" label="First name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} required />
          <TextField id="st-last" label="Last name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
        </div>
        <TextField id="st-email" label="Email (their sign-in)" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
        <SelectField id="st-role" label="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as StaffMember['role'] })} hint={ROLE_HINT[f.role]}>
          {(['EMPLOYEE', 'MANAGER', 'ADMIN'] as const).map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </SelectField>
        <TextField id="st-pass" label="Temporary password" type="text" autoComplete="off" value={f.password} minLength={10} onChange={(e) => setF({ ...f, password: e.target.value })} hint="At least 10 characters. Share it privately; they change it after signing in." required />
        <div className="form-actions">
          <Button type="submit" busy={busy}>
            Add
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ResetPasswordDialog({ member, onClose, onDone }: { member: StaffMember | null; onClose: () => void; onDone: (name: string) => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open={!!member} onClose={onClose} title={`New password for ${member?.firstName ?? ''}`}>
      <form
        className="form-grid"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!member) return;
          setBusy(true);
          setError(null);
          try {
            await api.request(`/v1/admin/staff/${member.id}/reset-password`, { method: 'POST', auth: true, body: { password } });
            onDone(member.firstName);
            setPassword('');
            onClose();
          } catch (err) {
            setError(errMsg(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        {error ? <Alert>{error}</Alert> : null}
        <TextField id="rp-pass" label="Temporary password" type="text" autoComplete="off" value={password} minLength={10} onChange={(e) => setPassword(e.target.value)} required />
        <p className="muted small">They’ll be signed out everywhere and should change it after signing in.</p>
        <div className="form-actions">
          <Button type="submit" busy={busy}>
            Set password
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Any staff member: change your own password (from the sidebar). */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  return (
    <Dialog open={open} onClose={onClose} title="Change your password">
      <form
        className="form-grid"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api.request('/v1/admin/me/password', { method: 'POST', auth: true, body: { currentPassword: current, newPassword: next } });
            setOk(true);
            setCurrent('');
            setNext('');
          } catch (err) {
            setError(errMsg(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        {error ? <Alert>{error}</Alert> : null}
        {ok ? <Alert tone="success">Password changed. You were signed out on other devices.</Alert> : null}
        <TextField id="cp-current" label="Current password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        <TextField id="cp-new" label="New password" type="password" autoComplete="new-password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} hint="At least 10 characters." required />
        <div className="form-actions">
          <Button type="submit" busy={busy}>
            Change password
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
